import React, { ChangeEvent, useCallback, useMemo } from "react";
import { MultiTextInput } from "@alm/flow-ds/components";
import { ElementComponentProps } from "elements/types";
import { ElementData, TextInputElement } from "records/Element";
import { useDispatch, useSelector } from "react-redux";
import { State } from "data";
import {
	createGetElementListData,
	ElementComponentData,
	ListElementComponentData,
} from "data/form/elementSelectors";
import { useStandardElement } from "components/Forms/Element";
import useDebounceMultiSetData from "hooks/useDebounceMultiSetData";
import { DisplayOnlyValue } from "elements";
import { jsonPatch } from "data/form/formActions";
import { PatchConfig } from "data/form/types";

function TextInputMulti({
	fid,
	vid,
	id,
	index,
	indexes,
	rowIndex,
	uuid,
	hint,
	label,
	required,
	disabled,
	validation,
	displayOnly,
	limit,
	elem,
}: ElementComponentProps<TextInputElement>) {
	const dispatch = useDispatch();
	const getListData = useMemo(createGetElementListData, []);

	const selectorProps = useMemo(
		() => ({ fid, vid, id, index, indexes, rowIndex, uuid }),
		[fid, vid, id, index, indexes, rowIndex, uuid]
	);

	const data = useSelector(
		(state: State) =>
			getListData(
				state,
				selectorProps
			) as ListElementComponentData<string>
	);

	const { eid } = useStandardElement({
		fid,
		vid,
		id,
		index,
		indexes,
		rowIndex,
		uuid,
	});

	const setDataAndQueue = useCallback(
		(current: ElementComponentData, next: ElementData) => {
			const op = current?.value === undefined ? "add" : "replace";

			const patch: PatchConfig[] = [
				{
					path: next.id,
					op,
					id: next.id,
					uuid,
					rowIndex,
					vid,
					data: next,
				},
			];
			dispatch(jsonPatch(fid, patch));
		},
		[dispatch, fid, rowIndex, uuid, vid]
	);

	const { items, onBlur, onChange } = useDebounceMultiSetData(
		setDataAndQueue,
		data.items
	);

	const createNewRow = useCallback(
		() =>
			({
				path: `${id}/-`,
				id: `${id}/-`,
				op: "add",
				data: { value: null },
				uuid,
				rowIndex,
				vid,
			} as PatchConfig),
		[id, rowIndex, uuid, vid]
	);

	const handleOnChange = useCallback(
		(event: ChangeEvent<HTMLInputElement>, index) => {
			const value = event.currentTarget.value;

			const formatValue = elem === "number" ? parseFloat(value) : value;

			const update = {
				id: `${id}/${index}`,
				value: formatValue,
			};

			onChange(update, index);
		},
		[elem, id, onChange]
	);

	const handleOnAdd = useCallback(() => {
		const patch: PatchConfig[] = [createNewRow()];
		// If data is undefined or empty then we create two new rows
		// so that the UX makes sense (one row is always visible even if there is no data)
		if (items.length === 0) patch.push(createNewRow());

		dispatch(jsonPatch(fid, patch));
	}, [createNewRow, items, dispatch, fid]);

	const handleOnDelete = useCallback(
		index => {
			if (
				data.value &&
				Array.isArray(data.value) &&
				data.value[index] !== undefined
			) {
				dispatch(
					jsonPatch(fid, [
						{
							path: `${id}/${index}`,
							id: `${id}/${index}`,
							op: "remove",
							uuid,
							rowIndex,
							vid,
						},
					])
				);
			}
		},
		[data.value, dispatch, fid, id, rowIndex, uuid, vid]
	);

	// Display only
	if (displayOnly) {
		return (
			<DisplayOnlyValue
				label={label}
				value={data.items.map(item => item.value)}
			/>
		);
	}

	return (
		<MultiTextInput
			id={eid}
			type={elem}
			values={items}
			maxLength={validation?.maxLength}
			onChange={handleOnChange}
			onBlur={onBlur}
			disabled={disabled}
			label={label}
			hint={hint}
			required={required}
			limit={limit ? limit : undefined}
			onDelete={handleOnDelete}
			onAdd={handleOnAdd}
		/>
	);
}

export default React.memo(TextInputMulti);
